import React, {useCallback, useEffect, useRef, useState} from 'react';
import { Formik, Form, Field } from 'formik';
import {Link, useNavigate, useParams} from 'react-router-dom';
import TitleMain from "../../components/Title/TitleMain";
import { useModal } from '../../contexts/ModalContext';
import {
    confirmProfileApi,
    getUtilisateurWithToken,
    sendConfirmMail, USER_STATUS, validateEmail, validateFormConfirm
} from "../../services/UserService";
import form_styles from "../../components/Form/Form.module.css";
import styles from "./ConfirmationPage.module.css";
import FieldCustom from "../../components/Field/FieldCustom";
import {useUser} from "../../contexts/UserContext";
import FieldPassword from "../../components/Field/FieldPassword";
import FieldCheckbox from "../../components/Field/FieldCheckbox";
import ReCAPTCHA from "react-google-recaptcha";
import Routing from "../../services/routing";
import DateService from "../../services/DateService";
import {logout} from "../../services/AuthService";
import stylesField from "../../components/Field/Field.module.css";



const validatePassword = (value) => {
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;
    if (!value) {
        return "Le mot de passe est requis.";
    }
    if (!passwordRegex.test(value)) {
        return "Le mot de passe doit contenir au moins 8 caractères, une minuscule, une majuscule et un chiffre.";
    }
    return null;
};

function ConfirmationProfilPage() {
    const { user } = useUser();
    const { showModal, hideModal } = useModal();
    const [utilisateur, setUtilisateur] = useState();
    const { token } = useParams();
    const { mail } = useParams();
    const [inputs, setInputs] = useState([]);
    const [initialValues, setInitialValues] = useState();
    const navigate = useNavigate();
    const [captchaLoaded, setCaptchaLoaded] = useState(false);
    const [captchaValue, setCaptchaValue] = useState('');
    const recaptchaRef = useRef(null);
    const [error, setError] = React.useState(null);

    const onCaptchaChange = (value) => {
        setCaptchaValue(value);
    };

    const handleCaptchaLoaded = () => {
        setCaptchaLoaded(true);
    };

    const renderCaptcha = () => {
        const captchaKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
        if (!captchaKey) {
            console.error("Captcha key is missing");
            return null;
        }

        return (
            <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={captchaKey}
                size="invisible"
                onChange={onCaptchaChange}
                asyncScriptOnLoad={handleCaptchaLoaded}
            />
        );
    };

    useEffect(() => {
        if (captchaLoaded && recaptchaRef.current) {
            recaptchaRef.current.execute();
        }
    }, [captchaLoaded]);



    const handleSuccess = (children = "Succès") => {
        showModal('success', {children: children, timeout: 3000});
    };
    const handleError = (children = "Une erreur est survenue") => {
        showModal('error', {children: children, timeout: 3000});
    };

    useEffect(() => {
        if (user && user.status === USER_STATUS.cree) {
            try {
                logout();
            } catch (error) {
                console.error('Error during logout:', error);
                return navigate(Routing.routes.home);
            }
        }
    }, [user]);

    useEffect(() => {
        if (!token || !mail) return;

        if(!validateEmail(mail)) {
            handleError("Email invalide");
            return;
        }

        const fetchUser = async () => {
            try {
                const response = await getUtilisateurWithToken(token, mail);
                if (response.success){
                    setUtilisateur(response.user);
                } else {
                    // handleError(response.msg);
                    console.error(response.msg);
                }
            } catch (error) {
                console.error('Erreur lors de la récupération des données de l\'utilisateur :', error);
            }
        }

        fetchUser();
    }, [token, mail]);

    useEffect(() => {
        if (!utilisateur) return;
        if (utilisateur.status === 1) {
            // Redirect to login page
            navigate(Routing.routes.login, { state: { email: utilisateur.email } });
        }
        if (![ -2, 0 ].includes(utilisateur.status)) return;

        const newInputs = [
            { type: 'text', name: 'nom', label: 'Nom', value: utilisateur.nom || ""},
            { type: 'text', name: 'prenom', label: 'Prénom', value: utilisateur.prenom || ""},
            { type: 'email', name: 'email', label: 'Email', value: utilisateur.email || ""},
            { type: 'number', name: 'mobile', label: 'Mobile', value: utilisateur.tel_mobile || ""},
            { type: 'date', name: 'birth_date', label: 'Date de naissance', value: DateService.formatDateWithoutTime(utilisateur.date_naissance) || "" },
            { type: 'password', name: 'password', label: 'Mot de passe', value: ""},
            { type: 'password', name: 'password_confirm', label: 'Confirmation du mot de passe', value: ""},
            { type: 'checkbox', name: 'cgu', label: <>Accepter les <Link to={Routing.routes.cgu} target={"_blank"}>CGU</Link></>, value: false},
            { type: 'hidden', name: 'token', label: '', value: token || ""},
            { type: 'hidden', name: 'captchaToken', label: '', value: "" },
        ];

        setInputs(newInputs);

        const newInitialValues = newInputs.reduce((acc, input) => ({
            ...acc,
            [input.name]: input.value,
        }), {});

        setInitialValues(newInitialValues);
    }, [utilisateur]);

    const handleSubmit = async (values, { setSubmitting }) => {
        if (!captchaValue) {
            handleError('Veuillez vérifier que vous n\'êtes pas un robot');
            setSubmitting(false);
            return;
        }


        if (validatePassword(values.password) !== null) {
            handleError('Le mot de passe doit contenir au moins 8 caractères, une majuscule, une minuscule et un chiffre.');
            setError('Le mot de passe doit contenir au moins 8 caractères, une majuscule, une minuscule et un chiffre.');
            return;
        }

        values.captchaToken = captchaValue;

        try {
            const response = await confirmProfileApi(values);
            if (response.success){
                handleSuccess(response.msg);

                setTimeout(() => {
                    navigate(Routing.routes.login, { state: { email: response.email } });
                }, 1500);
            } else {
                handleError(response.msg);
            }
        } catch (error) {
            handleError( "Une erreur est survenue : "+ error.message);
            console.error(error);
        }
        setSubmitting(false);
    };

    const handleSendConfirmMail = async () => {
        const response = await sendConfirmMail(mail);
        if (response.success){
            handleSuccess(response.msg);
        } else {
            handleError(response.msg);
        }
    };

    return (
        <div className="content">
            <TitleMain text="VOTRE PROFIL"/>
            {initialValues  ?
                <Formik
                    initialValues={initialValues}
                    validate={validateFormConfirm}
                    onSubmit={handleSubmit}
                >
                    {formik => (
                        <Form className={form_styles.form}>
                            <div className={stylesField.select}>
                                <span>Civilité</span>
                                <Field as="select" name="civilite">
                                    <option>
                                        -- Sélectionnez une civilité --
                                    </option>
                                    <option value={"4"}>
                                        Monsieur
                                    </option>
                                    <option value={"3"}>
                                        Madame
                                    </option>
                                </Field>
                            </div>

                            {inputs.map(({type, name, label}, index) => {
                                let FieldComponent = FieldCustom;
                                if (type === 'password') {
                                    FieldComponent = FieldPassword;
                                } else if (type === 'checkbox') {
                                    FieldComponent = FieldCheckbox;
                                }

                                return (
                                    <div key={index} style={{ width: "220px"}}>
                                        <FieldComponent name={name} label={label} type={type} />
                                        {error && name === 'password' && <div className="error" style={{
                                            color: "var(--clr-error)",
                                            fontSize: "var(--fs-secondary)",
                                            margin: "8px 16px 16px 16px"
                                        }}>{error}</div>}
                                    </div>
                                );
                            })}

                            {renderCaptcha()}

                            <button type="submit" className={`${form_styles.btnSubmit} btn`}>ENREGISTRER</button>
                        </Form>
                    )}
                </Formik>

                :
                <div className={styles.getUtilisateurFailure}>
                    <h4 className={styles.titleParagraph}>👋 Bienvenue ! </h4>
                    <p>
                        Pour commencer, activez votre compte en cliquant sur le lien que nous vous avons envoyé par e-mail.
                    </p>

                    <h4 className={styles.titleParagraph}>Rien dans votre boîte de réception ?</h4>
                    <p>
                        Jetez un œil dans vos spams ! Le lien d'activation est valable jusqu'à
                        minuit.
                    </p>

                    <p>
                        <button className={`btn ${form_styles.btnSubmit}`} onClick={handleSendConfirmMail}>Renvoyer le lien de confirmation</button>
                    </p>
                </div>

            }
        </div>
    );
}

export default ConfirmationProfilPage;
